import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  Platform,
  Modal,
  Linking,
  Animated,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { HomeScreenNavigationProp } from '../../types/navigation';
import { TimeSlot } from '../../types/timetable';
import { getTodayClasses, getNextClass, formatTimeRange } from '../../utils/timetableUtils';
import { useTimetableStore } from '../../store/timetableStore';
import { colors } from '../../constants/colors';
import { firebaseService } from '../../services/FirebaseService';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const SETTINGS_WIDTH = width * 0.85;

interface MainNUSTProps {
  navigation: HomeScreenNavigationProp;
}

const MainNUST: React.FC<MainNUSTProps> = ({ navigation }) => {
  const { timetable, initialize } = useTimetableStore();
  const [nextClass, setNextClass] = useState<TimeSlot | null>(null);
  const [todayClasses, setTodayClasses] = useState<TimeSlot[]>([]);
  const [menuVisible, setMenuVisible] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [username, setUsername] = useState<string>('');
  const slideAnim = React.useRef(new Animated.Value(width)).current;
  const fadeAnim = React.useRef(new Animated.Value(0)).current;

  useEffect(() => {
    initialize();
    loadUsername();
  }, []);

  const loadUsername = async () => {
    try {
      // First try to get from AsyncStorage
      const storedUsername = await AsyncStorage.getItem('@username');
      if (storedUsername) {
        setUsername(storedUsername);
      }

      // Then fetch from Firestore to ensure it's up to date
      const currentUser = firebaseService.getCurrentUser();
      if (currentUser) {
        const db = getFirestore();
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        
        if (userData?.username) {
          setUsername(userData.username);
          // Update AsyncStorage with latest username
          await AsyncStorage.setItem('@username', userData.username);
        }
      }
    } catch (error) {
      console.error('Error loading username:', error);
    }
  };

  useEffect(() => {
    const today = new Date().toLocaleString('en-US', { weekday: 'short' });
    const shortDay = today.slice(0, 3);
    const classes = timetable[shortDay] || [];
    
    const sortedClasses = [...classes].sort((a, b) => {
      const timeA = parseTime(a.time.split(' - ')[0]);
      const timeB = parseTime(b.time.split(' - ')[0]);
      return timeA.getTime() - timeB.getTime();
    });

    setTodayClasses(sortedClasses);

    const now = new Date();
    const next = sortedClasses.find(classSlot => {
      const classStartTime = parseTime(classSlot.time.split(' - ')[0]);
      return classStartTime > now;
    });

    setNextClass(next || null);
  }, [timetable]);

  useEffect(() => {
    const fetchUsername = async () => {
      const user = firebaseService.auth.currentUser;
      if (user) {
        try {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const userData = userDoc.data();
          console.log('User data:', userData); // Debug log
          if (userData && userData.username) {
            console.log('Setting username:', userData.username); // Debug log
            // Removed setUsername here
          }
        } catch (error) {
          console.error('Error fetching username:', error);
        }
      }
    };
    // Removed fetchUsername call here
  }, []);

  // Update countdown timer every second
  useEffect(() => {
    if (!nextClass) return;

    const updateCountdown = () => {
      const now = new Date();
      const classStartTime = parseTime(nextClass.time.split(' - ')[0]);

      // If class is tomorrow, add 24 hours
      if (classStartTime < now) {
        classStartTime.setDate(classStartTime.getDate() + 1);
      }

      const diff = classStartTime.getTime() - now.getTime();
      const hours24 = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(
        hours24 > 0 
          ? `${hours24}h ${mins}m ${secs}s`
          : `${mins}m ${secs}s`
      );
    };

    // Update immediately and then every second
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [nextClass]);

  const getNextClassMessage = () => {
    if (!nextClass) return "🎉 Freedom! No classes for today!";
    if (!countdown) return "Calculating...";
    return ` 📚 Next class starts in \n             ${countdown}`;
  };

  const handleContactUs = async () => {
    try {
      const email = 'support@lebantutech.com';
      const subject = 'Student Life Timetable Support';
      const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
      
      const canOpen = await Linking.canOpenURL(mailtoUrl);
      if (canOpen) {
        await Linking.openURL(mailtoUrl);
        hideMenu();
      } else {
        console.error('No email app available');
      }
    } catch (error) {
      console.error('Error opening email:', error);
    }
  };

  const showMenu = () => {
    setMenuVisible(true);
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const hideMenu = () => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: width,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setMenuVisible(false);
    });
  };

  const formatTime = (timeString: string) => {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours);
    
    // Convert to 24-hour format
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  };

  const renderTodayClasses = () => {
    if (todayClasses.length === 0) {
      return (
        <View style={styles.emptySchedule}>
          <Ionicons name="sunny-outline" size={48} color={colors.primary} />
          <Text style={[styles.emptyText, { color: colors.primary, fontSize: 18, fontWeight: '600', marginTop: 12 }]}>
            Yay! Time to relax! 
          </Text>
          <Text style={[styles.emptyText, { fontSize: 14, marginTop: 4, opacity: 0.7 }]}>
            No classes scheduled for today
          </Text>
        </View>
      );
    }

    return todayClasses.map((classItem, index) => {
      const [startTime, endTime] = classItem.time.split(' - ').map(formatTime);
      const timeDisplay = `${startTime} - ${endTime}`;

      return (
        <View key={index} style={styles.scheduleCard}>
          <View style={[styles.scheduleTime, { backgroundColor: classItem.color || colors.primary }]}>
            <Text style={styles.timeText}>{timeDisplay.split(' - ')[0]}</Text>
            <Text style={[styles.durationText, { color: 'rgba(255, 255, 255, 0.8)' }]}>{timeDisplay.split(' - ')[1]}</Text>
          </View>
          <View style={styles.scheduleDetails}>
            <Text style={styles.className}>{classItem.subject}</Text>
            {classItem.location && (
              <View style={styles.locationContainer}>
                <Ionicons name="location-outline" size={16} color={colors.textSecondary} />
                <Text style={styles.locationText}>{classItem.location}</Text>
              </View>
            )}
          </View>
        </View>
      );
    });
  };

  const parseTime = (timeStr: string) => {
    // Handle both 12-hour and 24-hour formats
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    
    let hour = hours;
    
    // Convert 12-hour format to 24-hour if period exists
    if (period) {
      if (period === 'PM' && hour !== 12) {
        hour += 12;
      } else if (period === 'AM' && hour === 12) {
        hour = 0;
      }
    }
    
    date.setHours(hour, minutes, 0, 0);
    return date;
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.greetingContainer}>
              <Text style={styles.greeting}>Welcome back, {username}!</Text>
              <View style={styles.titleContainer}>
                <Text style={styles.headerTitle}>TheNust</Text>
                <Text style={[styles.headerTitle, { color: colors.primary }]}>Life</Text>
              </View>
            </View>
            <View style={styles.headerRight}>
              <TouchableOpacity 
                style={styles.menuButton}
                onPress={showMenu}
              >
                <Ionicons name="menu" size={28} color={colors.text} />
              </TouchableOpacity>
            </View>
          </View>
          <View style={styles.headerDecoration} />
        </View>

        {/* Next Class Card */}
        <View style={styles.nextClassCard}>
          <View style={styles.nextClassContent}>
            <Text style={styles.nextClassTitle}>{getNextClassMessage()}</Text>
            {nextClass && (
              <Text style={styles.nextClassSubtitle}>{nextClass.subject} • {nextClass.location}</Text>
            )}
            {nextClass && (
              <Text style={styles.countdownText}>{countdown}</Text>
            )}
          </View>
        </View>

        {/* Quick Access */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Quick Access</Text>
            <TouchableOpacity onPress={() => navigation.navigate('Resources')}>
              <Text style={styles.seeAllButton}>See All</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.quickAccessGrid}>
            <TouchableOpacity
              style={styles.quickAccessItem}
              onPress={() => navigation.navigate('WebView', {
                url: 'https://ienabler.nust.na/pls/prodi41/w99pkg.mi_login',
                title: 'Student Portal'
              })}
            >
              <View style={[styles.iconContainer, { backgroundColor: 'rgba(74, 144, 226, 0.15)' }]}>
                <Ionicons name="desktop-outline" size={24} color={colors.secondary} />
              </View>
              <Text style={styles.quickAccessText}>Student Portal</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickAccessItem}
              onPress={() => navigation.navigate('WebView', {
                url: 'https://elearning.nust.na/login/index.php',
                title: 'E-Learning'
              })}
            >
              <View style={[styles.iconContainer, { backgroundColor: 'rgba(74, 144, 226, 0.15)' }]}>
                <Ionicons name="book-outline" size={24} color={colors.secondary} />
              </View>
              <Text style={styles.quickAccessText}>E-learning</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Today's Schedule */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Today's Schedule</Text>
            <TouchableOpacity 
              onPress={() => {
                const today = new Date().toLocaleString('en-US', { weekday: 'short' });
                const shortDay = today.slice(0, 3);
                navigation.navigate('Timetable', { initialDay: shortDay });
              }}
            >
              <Text style={styles.seeAllButton}>See All</Text>
            </TouchableOpacity>
          </View>
          
          <View style={styles.scheduleList}>
            {renderTodayClasses()}
          </View>
        </View>
      </ScrollView>

      {/* Settings Slide Screen */}
      {menuVisible && (
        <View style={StyleSheet.absoluteFill}>
          <Animated.View 
            style={[
              styles.overlay,
              {
                opacity: fadeAnim,
              },
            ]}
          >
            <TouchableOpacity
              style={{ flex: 1 }}
              activeOpacity={1}
              onPress={hideMenu}
            />
          </Animated.View>

          <Animated.View
            style={[
              styles.settingsContainer,
              {
                transform: [{ translateX: slideAnim }],
              },
            ]}
          >
            <SafeAreaView style={{ flex: 1 }}>
              <View style={styles.settingsHeader}>
                <Text style={styles.settingsTitle}>Settings</Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={hideMenu}
                >
                  <Ionicons name="close" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.settingsContent}>
                <TouchableOpacity 
                  style={styles.settingsItem}
                  onPress={handleContactUs}
                >
                  <View style={styles.settingsItemContent}>
                    <View style={styles.settingsItemLeft}>
                      <Ionicons 
                        name="mail-outline" 
                        size={24} 
                        color={colors.text}
                        style={styles.settingsItemIcon} 
                      />
                      <Text style={styles.settingsItemText}>Contact Us</Text>
                    </View>
                    <Ionicons 
                      name="chevron-forward" 
                      size={20} 
                      color={colors.textLight} 
                    />
                  </View>
                </TouchableOpacity>

                <View style={styles.settingsSection}>
                  <View style={styles.settingsInfo}>
                    <Text style={styles.settingsVersion}>Version 2.0.0</Text>
                    <Text style={styles.settingsCompany}> 2025 | By Lebantu Technologies CC</Text>
                  </View>
                </View>

                <View style={styles.logoutContainer}>
                  <TouchableOpacity 
                    style={styles.logoutButton}
                    onPress={async () => {
                      try {
                        await firebaseService.signOut();
                        hideMenu();
                      } catch (error) {
                        console.error('Logout error:', error);
                        alert('Failed to logout. Please try again.');
                      }
                    }}
                  >
                    <View style={styles.logoutContent}>
                      <Ionicons 
                        name="log-out-outline" 
                        size={24} 
                        color="#FFF"
                        style={styles.logoutIcon} 
                      />
                      <Text style={styles.logoutText}>Logout</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            </SafeAreaView>
          </Animated.View>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  header: {
    paddingTop: 20,
    paddingBottom: 15,
    backgroundColor: colors.surface,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    marginHorizontal: -20,
    paddingHorizontal: 20,
    position: 'relative',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  greetingContainer: {
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  greeting: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 4,
    fontWeight: '500',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.text,
    letterSpacing: -1,
  },
  notificationButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 51, 102, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  notificationDot: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.surface,
    zIndex: 1,
  },
  headerDecoration: {
    position: 'absolute',
    bottom: 0,
    left: 30,
    right: 30,
    height: 6,
    backgroundColor: colors.primary,
    opacity: 0.15,
    borderRadius: 3,
  },
  nextClassCard: {
    borderRadius: 16,
    marginVertical: 20,
    padding: 24,
    backgroundColor: colors.primary,
    ...Platform.select({
      ios: {
        shadowColor: colors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  nextClassContent: {
    flex: 1,
  },
  nextClassTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.surface,
    marginBottom: 8,
  },
  nextClassSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  countdownText: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '600',
    marginTop: 8,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
  },
  seeAllButton: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '500',
  },
  quickAccessGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  quickAccessItem: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 16,
    width: (width - 56) / 2,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  quickAccessText: {
    fontSize: 14,
    color: colors.text,
    fontWeight: '500',
  },
  scheduleList: {
    marginTop: 8,
  },
  scheduleCard: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 16,
    marginBottom: 12,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  scheduleTime: {
    width: 80,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.surface,
  },
  durationText: {
    fontSize: 12,
    marginTop: 4,
  },
  scheduleDetails: {
    flex: 1,
    padding: 16,
  },
  className: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  locationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  locationText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginLeft: 4,
  },
  emptySchedule: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    color: colors.textSecondary,
    marginTop: 12,
    textAlign: 'center',
  },
  menuButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  settingsContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: SETTINGS_WIDTH,
    backgroundColor: colors.background,
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: -2, height: 0 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  settingsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  settingsTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
  },
  closeButton: {
    padding: 4,
  },
  settingsContent: {
    flex: 1,
  },
  settingsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  settingsItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  settingsItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  settingsItemIcon: {
    marginRight: 12,
  },
  settingsItemText: {
    fontSize: 16,
    color: colors.text,
  },
  logoutContainer: {
    padding: 24,
    paddingBottom: 32,
  },
  logoutButton: {
    backgroundColor: colors.error,
    borderRadius: 16,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: colors.error,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  logoutContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  logoutIcon: {
    marginRight: 8,
  },
  logoutText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  settingsSection: {
    padding: 16,
  },
  settingsSectionTitle: {
    fontSize: 14,
    color: colors.textLight,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  settingsInfo: {
    marginTop: 8,
  },
  settingsVersion: {
    fontSize: 14,
    color: colors.text,
    marginBottom: 4,
  },
  settingsCompany: {
    fontSize: 14,
    color: colors.textLight,
  },
  menuIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: `${colors.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
    position: 'relative',
  },
});

export default MainNUST;
